import { useState } from 'react';
import { addMonths, isPast } from 'date-fns';

import {
  Box,
  Button,
  ButtonGroup,
  DateInput,
  DatePicker,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Label,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import { IconCalendarFilled } from '@zillow/constellation-icons';

import userPersonas from '@common/constants/userPersonas';
import Modal from '@common/components/ConstellationV10Modal';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import { validateDueDateInputs } from '../validation';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';

interface Props {
  saveStateToReduxStore: (e) => Promise<any>;
  onCancel: () => void;
  onSubmit: (editMode: boolean) => Record<string, any>;
  dueDate: string;
  editMode: boolean;
  fromDashboard: boolean;
  goToConfirmation: () => void;
  goToExperianFlow: () => void;
  setupNewTerm: boolean;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  onClose: () => void;
  togglePrevPage: () => void;
}

const DueDatePage = ({
  saveStateToReduxStore,
  onCancel,
  onSubmit,
  dueDate: initialDueDate,
  editMode = false,
  fromDashboard = false,
  goToConfirmation,
  goToExperianFlow,
  setupNewTerm = false,
  currentProperty,
  user,
  onClose,
  togglePrevPage,
}: Props) => {
  const [dueDate, setDueDate] = useState(initialDueDate === '' ? '' : formatDate(initialDueDate, 'MM/dd/yyyy'));
  const [errors, setErrors] = useState({ emptyError: '', pastDueDateError: '', pastCutoffDateError: '' });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const checkDateValid = (i: number | Date): boolean => {
    const currentDate = new Date();
    const cutoffDate = addMonths(currentDate, 2);
    return i <= cutoffDate;
  };

  const handleUpdate = async (): Promise<any> => {
    const validInputs = validateDueDateInputs({ dueDate, handleErrors: setErrors });

    if (!validInputs) {
      return;
    }

    IreneOnboardingCSEvents.emitContinueFromMICDueDateEvent({
      currentProperty,
      user,
    });

    await saveStateToReduxStore({
      dueDate: formatDate(dueDate, 'yyyy-MM-dd'),
    });
    const result = await onSubmit(editMode);
    if (setupNewTerm) {
      if (result && result.success) {
        if (result.experianFlowRequired) {
          goToExperianFlow();
        } else {
          goToConfirmation();
        }
      }
    }
  };

  const renderMainSection = () => (
    <Box>
      <Paragraph css={{ marginBottom: 'token(spacing.sm)' }} textStyle="body-lg-bold">
        When are your move-in related costs due?
      </Paragraph>
      <FormField
        description={
          errors.emptyError || errors.pastDueDateError || errors.pastCutoffDateError ? (
            <FormHelp error>{errors.emptyError || errors.pastDueDateError || errors.pastCutoffDateError}</FormHelp>
          ) : (
            <FormHelp>
              Your renter is charged when they send payment. The funds reach your account a few days later.
            </FormHelp>
          )
        }
        label={<Label>Due date</Label>}
      >
        <DatePicker
          closeModalOnDateSelect
          disabled={!checkDateValid}
          dateFormat="MM/dd/yyyy"
          disabledDates={(date) => isPast(date)}
          onOpenChange={(open) => setIsCalendarOpen(open)}
          open={isCalendarOpen}
          input={
            <DateInput
              css={{ paddingLeft: 'sm !important', paddingTop: 'xs !important', paddingBottom: 'xs !important' }}
              value={dueDate}
              readOnly
              onClick={() => setIsCalendarOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setIsCalendarOpen(true);
                }
              }}
            />
          }
          trigger={
            <TextButton tone="neutral">
              <Icon size="sm" render={<IconCalendarFilled />} />
            </TextButton>
          }
          onValueChange={(value) => setDueDate(String(value))}
          value={dueDate}
        />
      </FormField>
    </Box>
  );

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={onClose}
      header={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <TextButton textStyle="body-sm-bold" tone="brand" css={{ float: 'left' }} onClick={togglePrevPage}>
            Back
          </TextButton>
          <Heading level={3} textStyle="heading-xs" css={{ display: 'flex', justifyContent: 'center' }}>
            {fromDashboard ? 'Move-in costs' : 'Payment setup'}
          </Heading>
        </Box>
      }
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'token(spacing.sm) !important',
            padding: 'token(spacing.lg) !important',
          }}
        >
          {renderMainSection()}
          <FaqComponent faqs={FaqConstants.ONBOARDING_V2.MOVE_IN_COSTS.DUE_DATE_PAGE} />
        </Box>
      }
      footer={
        <ButtonGroup
          css={{
            padding: 'token(spacing.sm) !important',
          }}
          aria-label="Footer"
        >
          <Button emphasis="filled" onClick={handleUpdate}>
            {editMode && !fromDashboard ? 'Update' : 'Continue'}
          </Button>
          {editMode && !fromDashboard && (
            <Button emphasis="outlined" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </ButtonGroup>
      }
    />
  );
};

export default DueDatePage;
